import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { catchError, map, of, shareReplay } from 'rxjs';

import { PressKitAdapter } from '@modules/content-api/adapter/press-kit.adapter';
import { ContentAPIService } from '@modules/content-api/content-api.service';

export interface PressKitResultError {
  type: 'NOT_FOUND' | 'HTTP_ERROR' | 'NO_TRANSLATION';
}

export interface PressKitResult {
  data: ReturnType<PressKitAdapter['parse']> | null;
  error: PressKitResultError | null;
}

const PRESS_KIT_PAGE_PARAMS = new HttpParams().appendAll({
  include: [
    'articles.header_image.image',
    'articles.header_image.adjusted_image',
    'articles.header_image.alternate_images.image',
    'articles.header_image.alternate_images.adjusted_image',
    'articles.audios.audio',
    'articles.videos.video',
    'articles.videos.poster_image',
    'articles.images.image',
    'articles.images.adjusted_image',
    'articles.documents.file',
    'articles.technical_data.file',
    'articles.press',
    'contact_persons',
    'audios.audio',
    'documents.file',
    'technical_data.file',
    'images.image',
    'images.adjusted_image',
    'videos.video',
    'videos.poster_image',
    'main_image.image',
    'main_image.adjusted_image',
    'main_image.alternate_images.adjusted_image',
    'topic_tree',
  ].join(),
});

@Injectable({
  providedIn: 'root',
})
export class PressKitPageService {
  private apiService = inject(ContentAPIService);
  private pressKitAdapter = inject(PressKitAdapter);

  private mapError(error: HttpErrorResponse): PressKitResult {
    const status = error.status;
    if (status === 404) {
      return {
        data: null,
        error: {
          type: 'NOT_FOUND',
        },
      };
    }

    if (status === 406) {
      return {
        data: null,
        error: {
          type: 'NO_TRANSLATION',
        },
      };
    }

    return {
      data: null,
      error: {
        type: 'HTTP_ERROR',
      },
    };
  }

  public getPressKit(pressKitId: any) {
    return this.apiService.getResource('presskits', pressKitId, PRESS_KIT_PAGE_PARAMS).pipe(
      map((data) => {
        if (!data) {
          return {
            data: null,
            error: {
              type: 'NO_TRANSLATION',
            },
          };
        }

        try {
          return {
            data: this.pressKitAdapter.parse(data),
            error: null,
          };
        } catch {
          return {
            data: null,
            error: {
              type: 'HTTP_ERROR',
            },
          };
        }
      }),
      catchError((error) => of(this.mapError(error))),
      shareReplay(1)
    );
  }
}
